import { getEditLockReason } from "@/lib/invoiceActions";
import { PROCESSING_STATUS, REVIEW_STATUS } from "@/lib/statusEnums";

/**
 * Pure rules for the invoice detail screen. Everything that talks to the server
 * lives in `services/invoices/invoicesApi.js`; this file only decides what the
 * current status permits and how the form maps onto the API payload.
 */

/**
 * An extracted amount can carry a currency symbol or thousands separators
 * (for example "$1,250.50"). Clean those off when the form is seeded so an untouched value
 * is neither flagged as invalid nor silently misread on save; anything that is
 * still not a number is left as it came so the operator can see and correct it.
 */
function seedAmount(value) {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  const cleaned = text.replace(/^([-+]?)\s*[^\d.\-\s,A-Za-z]+\s*/, "$1").replace(/,/g, "");
  return getAmountError(cleaned) === null ? cleaned : text;
}

/** Values the form edits, seeded from the extracted fields. */
export function getInvoiceFormDefaults(detail) {
  const values = detail?.fieldValues ?? {};

  return {
    companyName: values.companyName ?? "",
    companyAddress: values.companyAddress ?? "",
    invoiceNumber: values.invoiceNumber ?? "",
    invoiceDate: detail?.invoiceDate ?? "",
    orderNumber: values.orderNumber ?? "",
    vatNumber: values.vatNumber ?? "",
    taxValue: seedAmount(values.taxValue),
    netAmount: seedAmount(values.netAmount),
    totalAmount: seedAmount(values.totalAmount),
    carriage: seedAmount(values.carriage),
    // true / false once decided, null while undecided. Only sample invoices use it.
    isApproved: detail?.isApproved ?? null,
  };
}

/** The monetary fields on the form, keyed as the form stores them. */
export const AMOUNT_FIELDS = ["taxValue", "netAmount", "carriage", "totalAmount"];

const AMOUNT_PATTERN = /^-?(\d+\.?\d*|\.\d+)$/;

/**
 * Blank is allowed (it is sent as null). Anything else must be a plain number:
 * `parseFloat` alone reads "12abc" as 12, so text typed into an amount would be
 * saved as a different value than the one on screen. Negatives are allowed for
 * credit notes; thousands separators and currency symbols are not.
 */
export function getAmountError(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (text === "") return null;
  return AMOUNT_PATTERN.test(text) ? null : "Enter a number, for example 1250.50.";
}

/** `{ fieldKey: message }` for every amount the user changed to something that is not a number. */
export function getAmountErrors(form, original) {
  const errors = {};
  AMOUNT_FIELDS.forEach((key) => {
    // A value the operator has not touched is not theirs to fix.
    if (String(form?.[key] ?? "").trim() === String(original?.[key] ?? "").trim()) return;
    const message = getAmountError(form?.[key]);
    if (message) errors[key] = message;
  });
  return errors;
}

/**
 * Approved / not approved is only asked of a sample invoice that is ready for
 * processing — earlier or later in its life the choice is optional.
 */
export function isApprovalRequired(detail) {
  return Boolean(detail?.isSampleInvoice) && detail.processingStatusId === PROCESSING_STATUS.READY_FOR_PROCESSING;
}

/** A sample invoice that is ready for processing must be marked approved or not approved before it can be saved. */
export function getApprovalError(detail, form) {
  if (!isApprovalRequired(detail)) return null;
  return typeof form?.isApproved === "boolean" ? null : "Select approved or not approved.";
}

/** Form state -> the body `PUT /invoices/:guid` expects. */
export function toUpdateInvoicePayload(form, { includeApproval = false } = {}) {
  const number = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return {
    number: form.invoiceNumber || null,
    date: form.invoiceDate || null,
    totalValue: number(form.totalAmount),
    netValue: number(form.netAmount),
    taxValue: number(form.taxValue),
    carriage: number(form.carriage),
    vatNumber: form.vatNumber || null,
    purchaseOrderNumber: form.orderNumber || null,
    companyName: form.companyName || null,
    companyAddress: form.companyAddress || null,
    // Left out until a choice is made, so an optional, unanswered choice is not sent as null.
    ...(includeApproval && typeof form.isApproved === "boolean" ? { isApproved: form.isApproved } : {}),
  };
}

/**
 * One rule for "can this be edited", shared with the queue row so the Edit
 * button and the form it opens can never disagree — see `getEditLockReason`.
 *
 * The old app expressed its half of this by comparing a review status id
 * against a *processing* status constant (`reviewStatus?.id ===
 * ProcessingStatus.SUCCESSFUL`); the two enums overlap numerically, so it
 * happened to behave, by accident.
 */
export function isInvoiceEditable(detail) {
  return getEditLockReason(detail) === null;
}

/**
 * Why the detail screen is read-only, or null when it is not. Permission is
 * checked ahead of status: being told an invoice is "queued for processing"
 * when the real reason is that you cannot edit any invoice is a wrong answer.
 */
export function getDetailLockReason(detail, { canUpdate, viewOnly }) {
  if (viewOnly) return null;
  if (!canUpdate) return "You do not have permission to edit invoices";
  // TEMP: status lock removed so the Edit button opens an editable form.
  // TODO: restore `return getEditLockReason(detail);` along with the Edit
  // button conditions (search "TEMP: status lock removed").
  return null;
}

export function needsApproval(reviewStatusId) {
  return reviewStatusId === REVIEW_STATUS.APPROVAL_REQUIRED;
}

export function canCompleteReview(reviewStatusId) {
  return (
    reviewStatusId === REVIEW_STATUS.AI_APPROVED ||
    reviewStatusId === REVIEW_STATUS.REVIEW_REQUIRED ||
    reviewStatusId === REVIEW_STATUS.APPROVAL_REQUIRED
  );
}

export function canApprove(reviewStatusId) {
  return needsApproval(reviewStatusId);
}

export function canReject(reviewStatusId) {
  return reviewStatusId !== REVIEW_STATUS.APPROVED && reviewStatusId !== REVIEW_STATUS.REJECTED;
}

/** The client name field is free text but restricted to letters and spaces. */
export const CLIENT_NAME_PATTERN = /^[A-Za-z\s]*$/;

export function isValidClientName(value) {
  return CLIENT_NAME_PATTERN.test(value);
}

/**
 * Whether the header's status and type pickers may open. Status needs the update
 * grant; type also needs the grant to read the categories it picks from. The
 * view-only route is read-only, so neither opens there.
 */
export function getQuickChangeAccess({ viewOnly, canUpdate, canReadCategories }) {
  const canChangeStatus = !viewOnly && Boolean(canUpdate);
  return {
    canChangeStatus,
    canRecategorise: canChangeStatus && Boolean(canReadCategories),
  };
}
